/**
 * System Claims related to permissions
 * 
 * These constants match the backend authorization claims defined in:
 * - Otelier.FactManager.Shared.Configuration.Constants.SystemClaimTypes
 * - Otelier.FactManager.Application.Common.Security.Permissions
 */

export const CLAIMS = {
  SECURITY: {
    FACTS: {
      PERMISSION: "Security:Facts:Permission",
      ROLE: "Security:Facts:Role",
    },
    WAREHOUSES: {
      PERMISSION: "Security:Warehouses:Permission",
      ROLE: "Security:Warehouses:Role",
    },
  },
} as const;

export const PERMISSION_TYPES = {
  READ: "Read",
  WRITE: "Write",
  PROVISION: "Provision",
} as const;

/**
 * Fact Manager Permissions
 * These match the backend Permissions class
 */
export const FACT_PERMISSIONS = {
  /** Permission to read facts */
  CAN_READ_FACTS: `${CLAIMS.SECURITY.FACTS.PERMISSION}:${PERMISSION_TYPES.READ}`,
  /** Permission to manage (create/update/delete) facts */
  CAN_MANAGE_FACTS: `${CLAIMS.SECURITY.FACTS.PERMISSION}:${PERMISSION_TYPES.WRITE}`,
} as const;

/**
 * Warehouse Permissions
 * These match the backend Permissions class
 */
export const WAREHOUSE_PERMISSIONS = {
  /** Permission to read warehouses */
  CAN_READ_WAREHOUSES: `${CLAIMS.SECURITY.WAREHOUSES.PERMISSION}:${PERMISSION_TYPES.READ}`,
  /** Permission to manage (create/update/delete) warehouses */
  CAN_MANAGE_WAREHOUSES: `${CLAIMS.SECURITY.WAREHOUSES.PERMISSION}:${PERMISSION_TYPES.WRITE}`,
} as const;

/**
 * Workflow Permissions
 * These match the backend Permissions class
 */
export const WORKFLOW_PERMISSIONS = {
  /** Permission to read workflows */
  CAN_READ_WORKFLOWS: `${CLAIMS.SECURITY.WAREHOUSES.PERMISSION}:${PERMISSION_TYPES.READ}`,
  /** Permission to manage (create/update/delete) workflows */
  CAN_MANAGE_WORKFLOWS: `${CLAIMS.SECURITY.WAREHOUSES.PERMISSION}:${PERMISSION_TYPES.WRITE}`,
} as const;

/**
 * Fact Manager Role Values
 */
export const FACT_ROLE_VALUES = {
  READ_ONLY: "ReadOnly",
  WRITE: "Write",
} as const;

/**
 * Fact Manager Role Names
 */
export const FACT_ROLE_NAMES = {
  READ_ONLY: "FactManager.Facts.ReadOnly",
  WRITE: "FactManager.Facts.Write",
} as const;

/**
 * Helper function to check if user has a specific permission
 */
export function hasPermission(userClaims: string[], permission: string): boolean {
  return userClaims.indexOf(permission) !== -1;
}

/**
 * Helper function to check if user has any of the specified permissions
 */
export function hasAnyPermission(userClaims: string[], permissions: string[]): boolean {
  return permissions.some(permission => userClaims.indexOf(permission) !== -1);
}

/**
 * Helper function to check if user has all of the specified permissions
 */
export function hasAllPermissions(userClaims: string[], permissions: string[]): boolean {
  return permissions.every(permission => userClaims.indexOf(permission) !== -1);
}
